import { motion } from "framer-motion";
import { IMAGES } from "@/assets/images";
import { Award, Globe, Users, Target, Film, Sparkles } from "lucide-react";
export default function About() {
  const achievements = [{
    year: "2014",
    title: "Studio Foundation",
    description: "Bibia Films International Studios established in Mumbai with state-of-the-art facilities"
  }, {
    year: "2016",
    title: "International Expansion",
    description: "Launched global collaboration program with international filmmakers"
  }, {
    year: "2018",
    title: "Technology Upgrade",
    description: "Invested in 8K cinema cameras and advanced post-production infrastructure"
  }, {
    year: "2020",
    title: "Award Recognition",
    description: "Multiple projects produced at our studio received international acclaim"
  }, {
    year: "2023",
    title: "Virtual Production",
    description: "Integrated cutting-edge virtual production and LED volume technology"
  }, {
    year: "2026",
    title: "Future-Facing Cinema",
    description: "Leading the industry in global storytelling and technological innovation"
  }];
  const values = [{
    icon: Globe,
    title: "Global Storytelling",
    description: "Creating one-globe narratives that transcend borders, cultures, and languages through shared human emotions"
  }, {
    icon: Film,
    title: "Artistic Excellence",
    description: "Balancing artistic integrity with global market sensibilities to deliver compelling cinematic experiences"
  }, {
    icon: Sparkles,
    title: "Innovation First",
    description: "Embracing cutting-edge technology and future-ready infrastructure to push creative boundaries"
  }, {
    icon: Users,
    title: "World-Class Collaboration",
    description: "Bringing together top directors, cinematographers, and artists from across the globe"
  }];
  return <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img src={IMAGES.FILM_PRODUCTION_2} alt="Film Production" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              About Bibia Films
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Future-Facing Cinema for a Global Audience
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-2xl -z-10" />
                <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2057075209181712384_keshhav_director_1_1.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Keshhav Panneriy - Founder & CEO" className="w-full object-top shadow-2xl h-[736px] object-cover" />
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }}>
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                  <Award className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Founder & Visionary</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold">
                  Keshhav Panneriy
                </h2>
                
                <div className="border-l-4 border-primary pl-6 py-4 bg-muted/50 rounded-r-lg">
                  <p className="text-lg italic text-foreground">"Cinema is more than entertainment—it is a universal language. At Bibia Film Studios, our purpose is to bridge local stories with international audiences through technology, craft, and vision."</p>
                </div>
                
                <p className="text-muted-foreground leading-relaxed">Founded by Keshhav Panneriy, Bibia Film Studios represents a bold vision for the future of cinema. With a commitment to creating globally relevant narratives that reflect the complexities and aspirations of the modern world, Keshhav has built an international film production company that transcends traditional boundaries.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Mission</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Developing future-facing cinema that reflects the realities of our interconnected world
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {values.map((value, index) => {
            const Icon = value.icon;
            return <motion.div key={value.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} className="group">
                  <div className="relative p-8 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 h-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="relative space-y-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold">{value.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Journey</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Milestones in building a world-class film production studio
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary" />
              
              <div className="space-y-12">
                {achievements.map((achievement, index) => <motion.div key={achievement.year} initial={{
                opacity: 0,
                x: -24
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: index * 0.1
              }} className="relative pl-20">
                    <div className="absolute left-0 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold shadow-lg">
                      {achievement.year}
                    </div>
                    
                    <div className="bg-background border border-border rounded-xl p-6 hover:border-primary/50 transition-colors duration-300">
                      <h3 className="text-2xl font-semibold mb-2">{achievement.title}</h3>
                      <p className="text-muted-foreground">{achievement.description}</p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto text-center">
            <Target className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Vision</h2>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              To be the leading international film production company that creates one-globe narratives—cinematic experiences built on shared human emotions that resonate across cultures, languages, and borders.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We bring together world-class directors, cinematographers, technicians, editors, designers, and artists from across the globe to craft stories that matter. Our commitment to artistic integrity, combined with cutting-edge technology and global market sensibilities, positions us at the forefront of future-facing cinema.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}